/**
 * @file 咨询与合作
 * @author FengGuang(fengguang01@baidu.com)
 */

import React, { useCallback } from 'react';
import { Button, Col, Form, Checkbox, Input, Radio, Row } from 'antd';
import { FormInstance, FormProps } from 'antd/es/form';
import { CheckboxGroupProps } from 'antd/es/checkbox/Group';
import classNames from 'classnames';
import { generateShouldUpdate } from '../../../../utils/antd-form-should-update';
import FuzzySearchInput from '../../../../components/fuzzysearch-input';

export interface IMainFormProps {
    verifyCodeImg: string;
    verifyCodeLoading: boolean;
    getVerifyCode?: () => void;
    onFinished?: (fieldsValue: any) => void;
    form: FormInstance;
}

const MainForm: React.FC<IMainFormProps> = props => {
    const { onFinished, form, verifyCodeLoading } = props;
    const { getFieldValue, getFieldsValue, resetFields, setFieldsValue } = form;

    // “是否在使用深度学习框架”变化后重置“使用的框架”和“遇到的问题”的错误信息
    const handleOnIsUsingDLFrameworkChange = useCallback(() => {
        const oldFields = getFieldsValue(['framework', 'problems']);
        resetFields(['framework', 'problems']);
        setFieldsValue(oldFields);
    }, [getFieldsValue, resetFields, setFieldsValue]);

    // 在框架这一项点击取消“其它”选项，则清空其它选项后面的输入框
    const handleOnFrameworkChange = useCallback<Exclude<CheckboxGroupProps['onChange'], undefined>>(
        value => {
            const oldValue = getFieldValue('framework') || [];
            if (oldValue.indexOf('other') > -1 && value.indexOf('other') === -1) {
                setFieldsValue({ frameworkOther: '' });
            }
        },
        [getFieldValue, setFieldsValue]
    );

    // 在遇到的问题这一项点击取消“其它”选项，则清空其它选项后面的输入框
    const handleOnProblemsChange = useCallback<Exclude<CheckboxGroupProps['onChange'], undefined>>(
        value => {
            const oldValue = getFieldValue('problems') || [];
            if (oldValue.indexOf('other') > -1 && value.indexOf('other') === -1) {
                setFieldsValue({ problemsOther: '' });
            }
        },
        [getFieldValue, setFieldsValue]
    );

    const handleOnFinish = useCallback<Exclude<FormProps['onFinish'], undefined>>(
        values => {
            const fvFrameowrk: string[] = values.framework || [];
            const fvFrameowrkHasOther = fvFrameowrk.indexOf('other') > -1;
            const framework = [
                ...fvFrameowrk.filter(item => item !== 'other'),
                fvFrameowrkHasOther ? values.frameworkOther : undefined
            ].filter(Boolean);

            const fvProblems: string[] = values.problems || [];
            const fvProblemsHasOther = fvProblems.indexOf('other') > -1;
            const problems = [
                ...fvProblems.filter(item => item !== 'other'),
                fvProblemsHasOther ? values.problemsOther : undefined
            ].filter(Boolean);

            onFinished &&
                onFinished({
                    companyName: values.companyName,
                    mainBusiness: values.mainBusiness,
                    scene: values.scene,
                    isUsingDLFramework: values.isUsingDLFramework,
                    framework,
                    problems,
                    name: values.name,
                    position: values.position,
                    phoneNumber: values.phoneNumber,
                    verifyCode: values.verifyCode
                });
        },
        [onFinished]
    );

    const { verifyCodeImg, getVerifyCode } = props;

    return (
        <Form onFinish={handleOnFinish} className="paddle-cac-form" layout="vertical" form={form} scrollToFirstError>
            <Form.Item label="您的企业基本信息" className="paddle-cac-form-companyInfor">
                <Form.Item name="companyName" initialValue="" rules={[{ required: true, message: '请输入企业名称' }]}>
                    {/* <Input placeholder="企业名称" /> */}
                    <FuzzySearchInput
                        placeholder="企业名称"
                        onChange={value => {
                            // onchange(value);
                        }}
                    />
                </Form.Item>
                <Form.Item
                    name="mainBusiness"
                    rules={[{ required: true, message: '请输入企业主营业务' }]}
                    initialValue=""
                >
                    <Input placeholder="企业主营业务" />
                </Form.Item>
                <Form.Item
                    name="scene"
                    rules={[
                        {
                            required: true,
                            message: '请输入场景'
                        }
                    ]}
                    initialValue=""
                >
                    <Input.TextArea placeholder="希望在哪些业务场景使用深度学习框架" rows={4} />
                </Form.Item>
            </Form.Item>
            <Form.Item
                label="您是否在使用深度学习框架："
                name="isUsingDLFramework"
                rules={[{ required: true, message: '请选择是否使用深度学习框架' }]}
                initialValue=""
            >
                <Radio.Group onChange={handleOnIsUsingDLFrameworkChange}>
                    <Radio value="1">是</Radio>
                    <Radio value="0">否</Radio>
                </Radio.Group>
            </Form.Item>
            <Form.Item noStyle shouldUpdate={generateShouldUpdate(['isUsingDLFramework'])}>
                {({ getFieldValue }) => {
                    // 在是否使用深度学习框架选项中，如果选中“是” ,就显示“框架选”项和“遇到的问题”选项
                    const isUsingDLFrameworkValue = getFieldValue('isUsingDLFramework') || 0;
                    const showFramework = isUsingDLFrameworkValue === '1';
                    const showProblems = isUsingDLFrameworkValue === '1';
                    return (
                        <React.Fragment>
                            <Form.Item
                                label="您使用的深度学习框架是："
                                style={{ display: showFramework ? 'unset' : 'none' }}
                                name="framework"
                                rules={[
                                    {
                                        required: showFramework,
                                        type: 'array',
                                        message: '请选择您使用的深度学习框架'
                                    }
                                ]}
                                initialValue={[]}
                            >
                                <Checkbox.Group onChange={handleOnFrameworkChange}>
                                    <Row className="radio-line">
                                        <Col span={8}>
                                            <Checkbox value="TensorFlow">TensorFlow</Checkbox>
                                        </Col>
                                        <Col span={8}>
                                            <Checkbox value="Caffe/Caffe2">Caffe/Caffe2</Checkbox>
                                        </Col>
                                        <Col span={8}>
                                            <Checkbox value="PaddlePaddle">PaddlePaddle</Checkbox>
                                        </Col>
                                    </Row>
                                    <Row className="radio-line">
                                        <Col span={8}>
                                            <Checkbox value="Torch/PyTorch">Torch/PyTorch</Checkbox>
                                        </Col>
                                        <Col span={8}>
                                            <Checkbox value="Keras">Keras</Checkbox>
                                        </Col>
                                        <Col span={8}>
                                            <Checkbox value="MxNet">MxNet</Checkbox>
                                        </Col>
                                    </Row>
                                    <Row className="radio-line">
                                        <Col span={24}>
                                            <Checkbox value="other">其它</Checkbox>
                                            <Form.Item noStyle shouldUpdate={generateShouldUpdate(['framework'])}>
                                                {({ getFieldValue }) => {
                                                    // 在框架选项中，如果选中“其它”,就显示后面的输入框，否则隐藏输入框
                                                    const frameworkValue = getFieldValue('framework') || [];
                                                    const showFrameworkOther = frameworkValue.indexOf('other') > -1;
                                                    return (
                                                        <span
                                                            style={{ display: showFrameworkOther ? undefined : 'none' }}
                                                        >
                                                            <Form.Item noStyle name="frameworkOther">
                                                                <Input className="framework-other-input" />
                                                            </Form.Item>
                                                        </span>
                                                    );
                                                }}
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Checkbox.Group>
                            </Form.Item>
                            <Form.Item
                                label="您在使用中遇到的问题是："
                                style={{ display: showProblems ? 'unset' : 'none' }}
                                name="problems"
                                rules={[{ required: showProblems, message: '请选择您遇到的问题' }]}
                                initialValue={[]}
                            >
                                <Checkbox.Group onChange={handleOnProblemsChange}>
                                    <Row className="radio-line">
                                        <Col span={8}>
                                            <Checkbox value="预测效果不理想">预测效果不理想</Checkbox>
                                        </Col>
                                        <Col span={8}>
                                            <Checkbox value="训练时间太长">训练时间太长</Checkbox>
                                        </Col>
                                        <Col span={8}>
                                            <Checkbox value="数据太少">数据太少</Checkbox>
                                        </Col>
                                    </Row>
                                    <Row className="radio-line">
                                        <Col span={8}>
                                            <Checkbox value="遇到bug">遇到bug</Checkbox>
                                        </Col>
                                        <Col span={8}>
                                            <Checkbox value="文档、社区不健全">文档、社区不健全</Checkbox>
                                        </Col>
                                    </Row>
                                    <Row className="radio-line">
                                        <Col span={24}>
                                            <Checkbox value="other">其它 </Checkbox>
                                            <Form.Item noStyle shouldUpdate={generateShouldUpdate(['problems'])}>
                                                {({ getFieldValue }) => {
                                                    // 在遇到的问题选项中，如果选中“其它”,就显示示后面的输入框，否则隐藏输入框
                                                    const problemsValue = getFieldValue('problems') || [];
                                                    const showProblemsOther = problemsValue.indexOf('other') > -1;
                                                    return (
                                                        <span
                                                            style={{ display: showProblemsOther ? undefined : 'none' }}
                                                        >
                                                            <Form.Item noStyle name="problemsOther">
                                                                <Input className="problems-other-input" />
                                                            </Form.Item>
                                                        </span>
                                                    );
                                                }}
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Checkbox.Group>
                            </Form.Item>
                        </React.Fragment>
                    );
                }}
            </Form.Item>
            <Form.Item label="我们如何联系您" className="contact-info">
                <Form.Item name="name" rules={[{ required: true, message: '请输入姓名' }]} initialValue="">
                    <Input placeholder="姓名" />
                </Form.Item>
                <Form.Item
                    name="position"
                    rules={[
                        {
                            required: true,
                            message: '请输入职位'
                        }
                    ]}
                    initialValue=""
                >
                    <Input placeholder="职位" />
                </Form.Item>
                <Form.Item
                    name="phoneNumber"
                    rules={[
                        { required: true, message: '请输入手机号' },
                        { pattern: /^1\d{10}/, message: '请输入正确的手机号' }
                    ]}
                    initialValue=""
                >
                    <Input placeholder="手机号" />
                </Form.Item>
                <Form.Item className="verify-code-form-item-wrap">
                    <Form.Item
                        className="verify-code-wrap"
                        name="verifyCode"
                        rules={[{ required: true, message: '请输入验证码' }]}
                        initialValue=""
                    >
                        <Input placeholder="请输入验证码" />
                    </Form.Item>

                    <div className="verify-code-img-wrap" onClick={getVerifyCode}>
                        <img alt="验证码" src={verifyCodeImg} />
                    </div>

                    <i
                        className={classNames(
                            'paddle-cac-form-item-code-reload',
                            verifyCodeLoading && 'paddle-feedback-form-item-code-reloading'
                        )}
                        onClick={getVerifyCode}
                    />
                </Form.Item>
            </Form.Item>
            <Form.Item>
                <Button className="submit-btn" type="primary" htmlType="submit">
                    提交
                </Button>
            </Form.Item>
        </Form>
    );
};

export default MainForm;
